import { useOpenPageInNewTab } from 'src/app/AppAuth/usePageNavigation'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import log from 'loglevel'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { useDocumentSourceContext } from 'src/context/DocumentSource/DocumentSourceContext'
import { showInfoToast, ToastPrompts } from 'src/Utils/helpers/toast'
import { useTestTableCsv } from 'src/components/async/document/useDocument'
import {
  EnforcementMode,
  HighlightCondition,
  ThresholdOperator,
} from '@trustero/trustero-api-web/lib/auditbotapi/auditbotapi_pb'
import { EvidenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'

import { Markup } from 'src/components/Reusable/Text/Markup'
import { GridRowClassNameParams } from '@mui/x-data-grid'
import {
  Source,
  SOURCE_RESULT,
} from '@trustero/trustero-api-web/lib/source/source_pb'
import { ControlCheckSourceTitle } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.components'
import { HelpPagesInternal } from 'src/pages/Help/Help.constants'
import { LearnMoreLink } from 'src/components/Reusable/Text/Link'
import { cleanTestProcedureDescription } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { convertRowSecondsToISODate } from 'src/Utils/grid/grid.helpers'
import { EvidenceDataGrid } from 'src/pages/Evidence/show/tabs/evidence/AutomaticEvidence/AutomaticEvidenceBody'
import {
  CsvDataGridContainer,
  TestTableDescription,
  TestTableDescriptionSection,
  TestTableDescriptionSectionHeader,
  TestTableResultText,
} from './evidence.styles'
import { TestTableStatus } from './evidence.constants'

export const getTestTableResult = (
  passed: boolean,
  count: number,
  threshold: number,
  mode: EnforcementMode,
  operator: ThresholdOperator,
): JSX.Element | null => {
  const isGreaterThan = operator === ThresholdOperator.GREATER_THAN_OR_EQUAL
  const showOperatorString =
    !(threshold === 100 && isGreaterThan) &&
    !(threshold === 0 && !isGreaterThan)
  const operatorString = showOperatorString
    ? operator === ThresholdOperator.GREATER_THAN_OR_EQUAL
      ? 'Greater than or equal to'
      : 'Less than or equal to'
    : ''
  const percentageString = mode === EnforcementMode.RELATIVE ? '%' : ''
  const roundedCount = count.toFixed(2)
  const roundedThreshold = threshold.toFixed(2)

  return (
    <TestTableResultText
      $status={passed ? TestTableStatus.PASS : TestTableStatus.FAIL}
    >
      <div>{`Expected: ${operatorString} ${roundedThreshold}${percentageString}`}</div>
      <div>{`Actual: ${roundedCount}${percentageString}`}</div>
    </TestTableResultText>
  )
}

export const ViewTestTableSourceModal = (): JSX.Element => {
  const openInNewTab = useOpenPageInNewTab()

  const { pageContext } = useParams()
  const { currentDocumentSource, setCurrentDocumentSource } =
    useDocumentSourceContext()
  const show = useIsShowModal(ModalFormId.VIEW_TEST_TABLE_SOURCE)
  const onHide = () => {
    setCurrentDocumentSource(null)
  }
  const hide = useHideModal({
    modalId: ModalFormId.VIEW_TEST_TABLE_SOURCE,
    onHide,
  })

  const sourceDoc: Source | null =
    currentDocumentSource instanceof Source ? currentDocumentSource : null

  const testTableInfo = sourceDoc?.getTestTable()
  const evidenceId = sourceDoc?.getId() || ''
  const contentId = testTableInfo?.getContentId() || ''
  const explanation = testTableInfo?.getExplanation() || ''
  const headerTooltip = useMemo(
    () => ({
      header: 'Result',
      tooltip: explanation,
    }),
    [explanation],
  )

  const {
    rows: rowsToUse,
    columns,
    isLoading,
    error,
  } = useTestTableCsv(
    contentId,
    !!contentId,
    explanation ? headerTooltip : undefined,
  )

  const rows = useMemo(
    () => rowsToUse.map((row) => convertRowSecondsToISODate(row)),
    [rowsToUse],
  )

  if (!sourceDoc || !testTableInfo) return <></>
  if (error) {
    log.error('Error fetching test table source', error)
    showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
    return <></>
  }
  const result = testTableInfo.getResult()
  const passed = result === SOURCE_RESULT.PASS
  const count = testTableInfo.getCount()
  const threshold = testTableInfo.getThreshold()
  const mode = testTableInfo.getMode()
  const operator = testTableInfo.getOperator()
  const highlightCondition = testTableInfo.getHighlightCondition()

  const goToSource = () => {
    openInNewTab(
      `/${pageContext}/${EvidenceAbsoluteRoutes.SHOW}/${encodeURIComponent(
        evidenceId,
      )}`,
    )
  }

  const description: JSX.Element = (
    <TestTableDescription>
      <TestTableDescriptionSection>
        This table shows how evidence from a table performed on a specific test
        procedure. &nbsp;
        <LearnMoreLink href={HelpPagesInternal['TestTable'].link} />
      </TestTableDescriptionSection>
      <TestTableDescriptionSection>
        {getTestTableResult(passed, count, threshold, mode, operator)}
      </TestTableDescriptionSection>
      <TestTableDescriptionSection>
        <TestTableDescriptionSectionHeader>
          Procedure
        </TestTableDescriptionSectionHeader>
        <Markup
          markdown={cleanTestProcedureDescription(testTableInfo.getTest())}
        />
      </TestTableDescriptionSection>
      <TestTableDescriptionSection>
        <TestTableDescriptionSectionHeader>
          Source
        </TestTableDescriptionSectionHeader>
        <ControlCheckSourceTitle
          source={sourceDoc}
          onClick={goToSource}
          result={result}
        />
      </TestTableDescriptionSection>
    </TestTableDescription>
  )

  return (
    <>
      <ModalForm
        show={show}
        hide={hide}
        formId={ModalFormId.VIEW_TEST_TABLE_SOURCE}
        title={testTableInfo.getName()}
        description={description}
        enforceFocus={false}
        openOnDrag={true}
        hideCancel
        size="xl"
        customSubmitButton={
          <StandardButton
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              hide()
            }}
            variant={StandardButtonVariant.SECONDARY}
            buttonSize={StandardButtonSize.MEDIUM}
          >
            Close
          </StandardButton>
        }
      >
        {isLoading ? (
          <TableListLoader />
        ) : (
          <CsvDataGridContainer $isModal={true}>
            <EvidenceDataGrid
              rows={rows}
              columns={columns}
              getRowClassName={(params: GridRowClassNameParams) => {
                const failed =
                  (highlightCondition === HighlightCondition.WHEN_FALSE &&
                    params.row['Result'] === 'False') ||
                  (highlightCondition === HighlightCondition.WHEN_TRUE &&
                    params.row['Result'] === 'True')
                return failed ? 'failed' : ''
              }}
            />
          </CsvDataGridContainer>
        )}
      </ModalForm>
    </>
  )
}
